import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import type {
  AckResult,
  IncomingChallenge,
  TeamLobbyInvite,
  TeamLobbyState,
} from "@mk/shared";
import { duelRulesSummary } from "../features/duel/rules.ts";
import { EMPTY_FRIENDS_DATA, type FriendsData } from "../features/friends/types.ts";
import { useTeamStore } from "../features/team/teamStore.ts";
import { errorMessage, messages } from "../i18n/index.ts";
import { api } from "../lib/api.ts";
import type { Screen } from "../lib/navigation.ts";
import { getSocket } from "../lib/socket.ts";

const DISMISSED_KEY = "mk-dismissed-invites";
const SAFE_SCREENS = new Set<Screen>(["home", "friends", "history", "profile"]);

type InviteItem =
  | { kind: "duel"; id: string; expiresAt: number; data: IncomingChallenge }
  | { kind: "team"; id: string; expiresAt: number; data: TeamLobbyInvite };

function loadDismissed(): Set<string> {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) ?? "[]") as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

function SwipeInviteCard({ item, now, busy, onDismiss, onRespond }: {
  item: InviteItem;
  now: number;
  busy: boolean;
  onDismiss: () => void;
  onRespond: (accept: boolean) => void;
}) {
  const [offset, setOffset] = useState(0);
  const startX = useRef<number | null>(null);
  const remaining = Math.max(0, Math.ceil((item.expiresAt - now) / 1000));
  const isTeam = item.kind === "team";
  const name = item.data.from.username;
  const detail = isTeam
    ? messages.invites.teamSummary(
        item.data.code,
        item.data.settings.durationMinutes,
        item.data.playerCount,
      )
    : item.data.mode === "race" ? messages.race.rules : duelRulesSummary(item.data.rules);

  function pointerDown(event: PointerEvent<HTMLElement>) {
    if ((event.target as Element).closest("button")) return;
    startX.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: PointerEvent<HTMLElement>) {
    if (startX.current === null) return;
    setOffset(Math.max(-140, Math.min(140, event.clientX - startX.current)));
  }

  function pointerEnd() {
    const shouldDismiss = Math.abs(offset) >= 72;
    startX.current = null;
    if (shouldDismiss) onDismiss();
    else setOffset(0);
  }

  return (
    <article
      className="invite-notification"
      data-kind={item.kind}
      style={{ transform: `translateX(${offset}px)`, opacity: 1 - Math.min(0.55, Math.abs(offset) / 260) }}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerEnd}
      onPointerCancel={pointerEnd}
    >
      <div className="invite-notification__head">
        <span className="eyebrow">{isTeam ? messages.invites.team : item.data.mode === "race" ? messages.race.title : messages.invites.duel}</span>
        <span>{messages.invites.remaining(remaining)}</span>
        <button type="button" onClick={onDismiss} aria-label={messages.invites.dismiss}>×</button>
      </div>
      <strong>{messages.invites.from(name)}</strong>
      <p>{detail}</p>
      <div className="invite-notification__actions">
        <button type="button" className="chip-btn" disabled={busy} onClick={() => onRespond(false)}>
          {messages.rematch.decline}
        </button>
        <button type="button" className="chip-btn chip-btn--primary" disabled={busy} onClick={() => onRespond(true)}>
          {messages.rematch.accept}
        </button>
      </div>
    </article>
  );
}

export function InviteNotifications({ screen, authed, onDuel, onTeam, onRace, onOpenAll }: {
  screen: Screen;
  authed: boolean;
  onDuel: () => void;
  onTeam: () => void;
  onRace: () => void;
  onOpenAll: () => void;
}) {
  const [data, setData] = useState<FriendsData>(EMPTY_FRIENDS_DATA);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now);

  const load = useCallback(async () => {
    if (!authed) {
      setData(EMPTY_FRIENDS_DATA);
      return;
    }
    try {
      setData(await api<FriendsData>("/api/friends"));
    } catch {
      // Bildirim katmanı ana ekranı bozmasın; socket sonraki değişimi getirir.
    }
  }, [authed]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!authed) return;
    const socket = getSocket();
    const addDuel = (challenge: IncomingChallenge) => setData((current) => ({
      ...current,
      challenges: [challenge, ...current.challenges.filter((item) => item.challengeId !== challenge.challengeId)],
    }));
    const addTeam = (invite: TeamLobbyInvite) => setData((current) => ({
      ...current,
      teamInvites: [invite, ...current.teamInvites.filter((item) => item.inviteId !== invite.inviteId)],
    }));
    const remove = ({ challengeId, inviteId }: { challengeId?: string; inviteId?: string }) => {
      const id = challengeId ?? inviteId;
      if (!id) return;
      setData((current) => ({
        ...current,
        challenges: current.challenges.filter((item) => item.challengeId !== id),
        teamInvites: current.teamInvites.filter((item) => item.inviteId !== id),
      }));
    };
    socket.on("connect", load);
    socket.on("friends:changed", load);
    socket.on("challenge:incoming", addDuel);
    socket.on("challenge:cancelled", remove);
    socket.on("team:invite:incoming", addTeam);
    socket.on("team:invite:cancelled", remove);
    return () => {
      socket.off("connect", load);
      socket.off("friends:changed", load);
      socket.off("challenge:incoming", addDuel);
      socket.off("challenge:cancelled", remove);
      socket.off("team:invite:incoming", addTeam);
      socket.off("team:invite:cancelled", remove);
    };
  }, [authed, load]);

  useEffect(() => {
    if (data.challenges.length + data.teamInvites.length === 0) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [data.challenges.length, data.teamInvites.length]);

  const invitations = useMemo<InviteItem[]>(() => [
    ...data.challenges.map((challenge) => ({
      kind: "duel" as const,
      id: challenge.challengeId,
      expiresAt: challenge.expiresAt,
      data: challenge,
    })),
    ...data.teamInvites.map((invite) => ({
      kind: "team" as const,
      id: invite.inviteId,
      expiresAt: invite.expiresAt,
      data: invite,
    })),
  ].filter((item) => item.expiresAt > now && !dismissed.has(item.id))
    .sort((a, b) => a.expiresAt - b.expiresAt), [data.challenges, data.teamInvites, dismissed, now]);

  const dismiss = (id: string) => setDismissed((current) => {
    const next = new Set(current).add(id);
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
    } catch {
      // Depolama kapalıysa yalnız bu render oturumunda gizlemek yeterli.
    }
    return next;
  });

  const respond = (item: InviteItem, accept: boolean) => {
    if (busyId) return;
    setBusyId(item.id);
    setNotice(null);
    const socket = getSocket();
    if (item.kind === "duel") {
      socket.emit("challenge:respond", { challengeId: item.id, accept }, (response: AckResult<unknown>) => {
        setBusyId(null);
        if (!response.ok) return setNotice(errorMessage(response));
        setData((current) => ({ ...current, challenges: current.challenges.filter((entry) => entry.challengeId !== item.id) }));
        if (accept) (item.data.mode === "race" ? onRace : onDuel)();
      });
      return;
    }
    socket.emit("team:invite:respond", { inviteId: item.id, accept }, (response: AckResult<{ lobby: TeamLobbyState | null }>) => {
      setBusyId(null);
      if (!response.ok) return setNotice(errorMessage(response));
      setData((current) => ({ ...current, teamInvites: current.teamInvites.filter((entry) => entry.inviteId !== item.id) }));
      if (accept && response.data.lobby) {
        useTeamStore.getState().setLobby(response.data.lobby);
        onTeam();
      }
    });
  };

  if (!authed || !SAFE_SCREENS.has(screen) || invitations.length === 0) return null;
  const shown = invitations.slice(0, 2);
  const overflow = invitations.length - shown.length;
  return (
    <aside className="invite-stack" aria-label={messages.invites.title}>
      {notice && <p className="invite-stack__notice" role="alert">{notice}</p>}
      {shown.map((item) => (
        <SwipeInviteCard
          key={item.id}
          item={item}
          now={now}
          busy={busyId === item.id}
          onDismiss={() => dismiss(item.id)}
          onRespond={(accept) => respond(item, accept)}
        />
      ))}
      {overflow > 0 && (
        <button type="button" className="invite-stack__more" onClick={onOpenAll}>
          {messages.invites.more(overflow)}
        </button>
      )}
    </aside>
  );
}
